import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const LedgerModal = ({
  isOpen,
  onClose,
  customer,
  fetchCustomerTransactions,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState({
    rows: [],
    totalCredit: 0,
    totalDebit: 0,
    finalBalance: 0,
    customerWill: "Pay",
  });

  const calculateLedger = useCallback((txns) => {
    let totalCredit = customer?.openingBalance || 0;
    let totalDebit = 0;
    let runningBalance = customer?.openingBalance || 0;

    const rows = txns.map((t) => {
      const credit =
        (parseFloat(t?.summary?.grandTotal)) || 0;
      const debit = parseFloat(t?.summary?.paymentDone) || 0;

      totalCredit += credit;
      totalDebit += debit;
      runningBalance = totalCredit - totalDebit;

      return {
        date: `${t?.transactionDetails?.billNo} - ${t.transactionDetails?.gstApplicable === true ? "TAX INVOICE" : "CASH MEMO"}`,
        credit: credit.toFixed(2),
        debit: debit.toFixed(2),
        balance: runningBalance.toFixed(2),
      };
    });

    setLedgerData({
      rows,
      totalCredit: totalCredit.toFixed(2),
      totalDebit: totalDebit.toFixed(2),
      finalBalance: runningBalance.toFixed(2),
      customerWill: runningBalance >= 0 ? "Pay" : "Receive",
      customerPayAmount: Math.abs(runningBalance).toFixed(2),
    });
  }, [customer?.openingBalance]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCustomerTransactions(customer.id);
      const txns = data || [];
      // setTransactions(txns);
      calculateLedger(txns);
    } catch (error) {
      console.error("Failed to load transactions:", error);
      // setTransactions([]);
      calculateLedger([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.id, fetchCustomerTransactions, calculateLedger]);


  // Fetch transactions when modal opens
  useEffect(() => {
    if (isOpen && customer?.id) {
      loadLedger();
    }
  }, [isOpen, customer?.id, loadLedger]);

  // const formatDateForDisplay = (dateStr) => {
  //   if (!dateStr) return "-";
  //   return dateStr;
  // };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: isDark
            ? "rgba(30, 30, 30, 0.95)"
            : "rgba(255, 255, 255, 0.9)",
          borderRadius: "24px",
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(148, 163, 184, 0.6)",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(20px)",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          p: "18px 20px 16px",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          background: "rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "10px",
          mb: "10px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: isDark ? "#e0e0e0" : "#020617",
              m: 0,
            }}
          >
            Customer Ledger
          </Typography>
          <Typography
            sx={{
              mt: "2px",
              fontSize: "13px",
              color: isDark ? "#a0a0a0" : "#64748b",
            }}
          >
            Detailed statement of credit, debit and running balance.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            border: "none",
            background: isDark
              ? "rgba(50, 50, 50, 0.96)"
              : "rgba(248, 250, 252, 0.96)",
            borderRadius: "999px",
            width: "32px",
            height: "32px",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isDark ? "#a0a0a0" : "#475569",
            boxShadow: "0 8px 18px rgba(148, 163, 184, 0.4)",
            transition: "background 0.15s ease, transform 0.08s ease",
            "&:hover": {
              background: "#fee2e2",
              color: "#b91c1c",
              transform: "translateY(-1px)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflow: "hidden",
          flex: 1,
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={40} sx={{ color: "#2563eb", mb: 2 }} />
            <Typography sx={{ color: isDark ? "#a0a0a0" : "#6b7280" }}>
              Loading ledger...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Customer Info */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "6px 14px",
                fontSize: "13px",
                background: isDark
                  ? "rgba(40, 40, 40, 0.96)"
                  : "rgba(255, 255, 255, 0.96)",
                borderRadius: "16px",
                p: "8px 10px",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(226, 232, 240, 0.9)",
                color: isDark ? "#e0e0e0" : "#1a1a1a",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "13px", m: 0 }}>
                  <strong>Customer Name:</strong> {customer?.name || "-"}
                </Typography>
                <Typography sx={{ fontSize: "13px", m: 0 }}>
                  <strong>Address:</strong> {customer?.address || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "13px", m: 0 }}>
                  <strong>Mobile:</strong> {customer?.contact || "-"}
                </Typography>
              </Box>
            </Box>

            {/* Table Wrapper */}
            <Box
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                overflowY: "auto",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(226, 232, 240, 0.9)",
                background: isDark
                  ? "rgba(40, 40, 40, 0.98)"
                  : "rgba(255, 255, 255, 0.98)",
                boxShadow: "0 10px 24px rgba(148, 163, 184, 0.25)",
                maxHeight: "48vh",
              }}
            >
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                  "& th, & td": {
                    p: "8px 10px",
                    textAlign: "right",
                  },
                  "& th:first-of-type, & td:first-of-type": {
                    textAlign: "left",
                  },
                  "& thead": {
                    background: isDark
                      ? "linear-gradient(120deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))"
                      : "linear-gradient(120deg, rgba(239, 246, 255, 0.98), rgba(224, 231, 255, 0.98))",
                  },
                  "& th": {
                    fontWeight: 600,
                    color: isDark ? "#e0e0e0" : "#4b5563",
                    borderBottom: isDark
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(226, 232, 240, 0.9)",
                  },
                  "& tbody tr:nth-of-type(even)": {
                    background: isDark ? "rgba(50, 50, 50, 0.5)" : "#f9fafb",
                  },
                  "& tbody tr:hover": {
                    background: isDark ? "rgba(37, 99, 235, 0.1)" : "#eef4ff",
                  },
                  "& td": {
                    color: isDark ? "#e0e0e0" : "#1a1a1a",
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Bill no.</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Opening Balance Row */}
                  <tr>
                    <td>
                      <strong>Opening Balance</strong>
                    </td>
                    <td></td>
                    <td></td>
                    <td>{customer?.openingBalance || 0}</td>
                  </tr>

                  {/* Transaction Rows */}
                  {ledgerData.rows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.date}</td>
                      <td>{row.credit}</td>
                      <td>{row.debit}</td>
                      <td>{row.balance}</td>
                    </tr>
                  ))}

                  {ledgerData.rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: isDark ? "#a0a0a0" : "#6b7280",
                        }}
                      >
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Box>
            </Box>

            {/* Summary Cards */}
          </>
        )}
      </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "10px",
                mt: "10px",
              }}
            >
              {/* Total Credit */}
              <Box
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "14px",
                  p: "8px 10px",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  boxShadow:
                    "2px 8px 8px rgba(148, 163, 184, 0.28),0px -2px 8px rgba(255, 255, 255, 0.55)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: isDark ? "#a0a0a0" : "#6b7280",
                    mb: "3px",
                  }}
                >
                  Total Credit
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isDark ? "#4ade80" : "#111827",
                  }}
                >
                  ₹{ledgerData.totalCredit}
                </Typography>
              </Box>

              {/* Total Debit */}
              <Box
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "14px",
                  p: "8px 10px",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  boxShadow:
                    "2px 2px 8px rgba(148, 163, 184, 0.28), -2px -2px 8px rgba(255, 255, 255, 0.95)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: isDark ? "#a0a0a0" : "#6b7280",
                    mb: "3px",
                  }}
                >
                  Total Debit
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isDark ? "#f87171" : "#111827",
                  }}
                >
                  ₹{ledgerData.totalDebit}
                </Typography>
              </Box>

              {/* Final Balance */}
              <Box
                sx={{
                  background: isDark
                    ? "rgba(40, 40, 40, 0.96)"
                    : "rgba(255, 255, 255, 0.96)",
                  borderRadius: "14px",
                  p: "8px 10px",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(226, 232, 240, 0.9)",
                  boxShadow:
                    "2px 2px 8px rgba(148, 163, 184, 0.28), -2px -2px 8px rgba(255, 255, 255, 0.95)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: isDark ? "#a0a0a0" : "#6b7280",
                    mb: "3px",
                  }}
                >
                  Final Balance
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isDark ? "#60a5fa" : "#111827",
                  }}
                >
                  ₹{ledgerData.finalBalance}
                </Typography>
              </Box>

              {/* Customer Will Pay/Receive */}
              <Box
                sx={{
                  background: isDark
                    ? "rgba(127, 29, 29, 0.3)"
                    : "rgba(254, 242, 242, 0.95)",
                  borderRadius: "14px",
                  p: "8px 10px",
                  border: isDark
                    ? "1px solid rgba(248, 113, 113, 0.4)"
                    : "1px solid rgba(248, 113, 113, 0.6)",
                  boxShadow:
                    "2px 8px 8px rgba(148, 163, 184, 0.28),0px -2px 8px rgba(255, 255, 255, 0.75)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: isDark ? "#fca5a5" : "#6b7280",
                    mb: "3px",
                  }}
                >
                  Customer Will{" "}
                  <span style={{ fontWeight: 600 }}>
                    {ledgerData.customerWill}
                  </span>
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#b91c1c",
                  }}
                >
                  ₹{ledgerData.customerPayAmount || "0.00"}
                </Typography>
              </Box>
            </Box>
    </Dialog>
  );
};

export default LedgerModal;
